/**
 * Owner: Masa Bayu
 * File: BookCard.tsx
 * Date:Time: 2026-10-22 23:52
 * Description: Kad buku marketplace — kulit seni CSS asli, harga sebenar, CTA beli.
 *   §466 S1: penarafan/lencana rekaan dibuang; label mengikut locale.
 */

import type { ReactElement } from 'react';
import { useHubCoverSrc } from '../../publication/use-hub-cover';
import { LandingIcon } from './landing/LandingIcons';
import type { MarketplaceBook } from './marketplace-projection';
import { formatMarketplacePriceOrUnknown, type MarketplaceLabels } from './marketplace-locale';

export interface BookCardProps {
  readonly t: MarketplaceLabels;
  readonly book: MarketplaceBook;
  readonly added: boolean;
  /** §466 S2 — false = tiada tawaran hub; CTA beli tidak dipaparkan (bukan butang mati). */
  readonly purchasable?: boolean;
  readonly onNavigate: (path: string) => void;
  readonly onBuy: (book: MarketplaceBook) => void;
}

export function BookCard({
  t,
  book,
  added,
  purchasable = true,
  onNavigate,
  onBuy,
}: BookCardProps): ReactElement {
  const detailPath = `/publication/${encodeURIComponent(book.publicationId)}`;
  const coverSrc = useHubCoverSrc(book.publicationId);
  const openDetail = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    onNavigate(detailPath);
  };
  return (
    <li className="hmk__card">
      <a className="hmk__cover" data-tone={book.coverTone} href={detailPath} onClick={openDetail}>
        {/* COVER-A3 — cover sebenar dari bundle; kotak nada hanya lalai bila tiada cover. */}
        {coverSrc === null ? (
          <>
            <span className="hmk__cover-title">{book.title}</span>
            <span className="hmk__cover-pattern" aria-hidden="true" />
          </>
        ) : (
          <img className="hmk__cover-img" src={coverSrc} alt={book.title} />
        )}
      </a>
      <a className="hmk__card-title" href={detailPath} onClick={openDetail}>
        {book.title}
      </a>
      {/* §466 S1 — penulis dipaparkan hanya bila hub menghantarnya; dahulu
          'QX Author' + bintang/lencana dijana daripada hash id (rekaan). */}
      {book.author !== null ? <p className="hmk__card-author">{book.author}</p> : null}
      {/* §466 S1 — kod mata wang ikut descriptor hub (bukan profil pelawat):
          harga USD tidak boleh dipaparkan sebagai 'RM'. */}
      <p className="hmk__card-price">
        {formatMarketplacePriceOrUnknown(book.priceMinorUnits, book.currency, t.card.priceUnknown)}
      </p>
      {/* §466 S2 — tiada tawaran hub → tiada CTA beli (bukan butang yang mati). */}
      {purchasable ? (
        <button
          type="button"
          className="hmk__buy-btn"
          data-added={added}
          onClick={() => {
            onBuy(book);
          }}
        >
          <LandingIcon name="cart" size={15} />{' '}
          {added ? (
            <>
              {t.card.inCart} <LandingIcon name="check" size={13} />
            </>
          ) : (
            t.card.buyNow
          )}
        </button>
      ) : null}
    </li>
  );
}
